<template>
  <!--
    `variant="head"` draws the head row alone: a host that already owns a panel
    of its own (the dashboard `device-control` card, the room page's
    full-control card) adopts the head without a second surface. `variant="panel"`
    is the modal's shape: the glass sheet + head + the body slot.
  -->
  <div
    class="device-sheet"
    :class="[
      `device-sheet--${variant}`,
      {
        'device-sheet--active': active,
        'device-sheet--alarm': alarmTint,
        'device-sheet--motion': motion,
        'device-sheet--mono': monoIdle,
        'glass-surface': variant === 'panel',
      },
    ]"
    :style="rootStyle">
    <div class="device-sheet-head">
      <div class="device-sheet-circle" :class="{ 'device-sheet-circle--active': active }">
        <img v-if="thumbnailUrl" class="device-sheet-thumb" :src="thumbnailUrl" alt="" />
        <span v-else class="material-symbols-outlined device-sheet-glyph" aria-hidden="true">{{ glyph }}</span>
        <!-- The motion reading, in the light widget's own language (a walking
             person, amber, pulsing) so the two surfaces cannot drift. -->
        <span v-if="motion" class="device-sheet-motion-badge material-symbols-outlined" aria-hidden="true"
          >directions_walk</span
        >
      </div>

      <div class="device-sheet-title">
        <h3 class="device-sheet-name">{{ name }}</h3>
        <!-- A device with no room keeps the row out of the head entirely; a host
             that wants an empty-state word fills the `room` slot instead. -->
        <button v-if="room" type="button" class="device-sheet-room" @click="$emit('navigate-room')">
          <ControlIcon name="pin" size="0.85em" />
          <span class="device-sheet-room-name"
            ><slot name="room">{{ room }}</slot></span
          >
        </button>
        <span v-else class="device-sheet-room device-sheet-room--empty"><slot name="room" /></span>
      </div>

      <div class="device-sheet-right">
        <span v-if="stateText" class="device-sheet-pill" :class="{ 'device-sheet-pill--off': !active }">
          {{ stateText }}
        </span>
        <slot name="actions" />
        <!-- The gear opens the device EDITOR — a settings surface a viewer never
             gets, so it is not offered to one (card t_803e86a4). Every host
             gives it a meaning: the modal opens the device editor, the room
             page's head its settings modal, the dashboard card its
             `DeviceSettingsModal` (card t_f60a8a96). -->
        <button
          v-if="!isViewer"
          type="button"
          class="device-sheet-iconbtn device-sheet-iconbtn--gear"
          :title="tt('Device Settings', 'Nastavitve naprave')"
          @click="$emit('open-settings')">
          <ControlIcon name="gear" />
        </button>
        <!-- The close is a MODAL affordance (card t_f60a8a96): a host that has
             nothing to dismiss does not draw one — an inert close is the defect
             this flag exists for. -->
        <button
          v-if="dismissible"
          type="button"
          class="device-sheet-iconbtn"
          :title="tt('Close', 'Zapri')"
          @click="$emit('close')">
          <ControlIcon name="close" />
        </button>
      </div>
    </div>

    <div v-if="variant === 'panel'" class="device-sheet-body">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import ControlIcon from '../device-icons/ControlIcon.vue';
import { sheetAccentStyle } from './sheetAccent';
import { tt } from '../../utils/consoleText';

const props = withDefaults(
  defineProps<{
    /** `panel` = the whole surface (head + body); `head` = the head row only. */
    variant: 'panel' | 'head';
    /** The device's name, as the tiles show it. */
    name: string;
    /** The room's name; empty draws no room row (fill the `room` slot for an empty state). */
    room: string;
    /** The device's own Material glyph — the head circle's fallback art. */
    glyph: string;
    /** A device thumbnail; when set it REPLACES the glyph in the circle. */
    thumbnailUrl?: string;
    /** The sheet's accent; every primitive inside reads it. Default: the light accent. */
    accent?: string;
    /** Whether the device is doing something — drives the circle tint and the pill. */
    active: boolean;
    /** The right-side pill's text; empty draws no pill. */
    stateText?: string;
    /** The alarm flash kept from the old modal — the alarm wins over the accent. */
    alarmTint?: boolean;
    /**
     * The device is reporting motion right now (occupancy/presence sensors): the
     * card breathes and the circle carries the walking person. Owner, 24.10.2026:
     * "we would need some kind of warning indication on the device widget cards.
     * like breathing bg,, border.." — the same cadence as `.motion-indicator`
     * (2.2 s), so the light widget and the sensor card read as one system.
     */
    motion?: boolean;
    /**
     * The thumbnail is a picture of a ROOM (a motion sensor's view), not of the
     * device: it reads monochrome while idle and returns to full colour on
     * `active`. Owner, 24.10.2026: "it should be black end white. and on motion we
     * turn it in color".
     */
    monoIdle?: boolean;
    /** A guest (viewer) keeps the controls and never gets the gear. */
    isViewer: boolean;
    /**
     * Whether this host can be dismissed at all (card t_f60a8a96) — the close
     * is a modal affordance, so every host states its answer: the modal passes
     * `true`; the room page's head and the dashboard card pass `false` and draw
     * no close (an inert one was the defect). Required on purpose, like
     * `isViewer`: a new host should not inherit either answer by accident.
     */
    dismissible: boolean;
  }>(),
  {
    accent: 'var(--device-color-light)',
    thumbnailUrl: '',
    stateText: '',
    alarmTint: false,
    motion: false,
    monoIdle: false,
  },
);

defineEmits<{
  close: [];
  'open-settings': [];
  'navigate-room': [];
}>();

const rootStyle = computed(() => sheetAccentStyle(props.accent));
</script>

<style scoped>
/*
 * The panel is the modal's glass sheet. Two layers of surface work here on
 * purpose: this base recipe paints the `normal` card style (a solid panel, like
 * every other normal-mode card), and the GLOBAL `.glass-surface` class the panel
 * carries (`src/style.css`) repaints it in `glass` mode, including the blur-off
 * case — that rule out-specifies this one, so the sheet never fights the theme
 * store's own presets.
 */
.device-sheet {
  color: rgb(var(--gray-800));
  --sheet-accent: var(--device-color-light);
  /* The head answers its OWN width (card t_f60a8a96): a host never has to
     compute a pixel flag — the dashboard `device-control` card's declared
     minimum is 154×86 (132 px inside the card's padding and border) and the
     modal is a phone-width sheet, and both are this one component. Inline-size
     containment means the host must impose the width (all three do: the
     modal's `w-full max-w-2xl`, the card's tile, the room page's block) —
     a shrink-to-fit wrapper would size the sheet by its padding. Named, so no
     ancestor `@container` (the room page's section cards) can capture the
     queries. */
  container: device-sheet / inline-size;
}

.dark .device-sheet {
  color: rgb(var(--gray-100));
}

.device-sheet--panel {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  border-radius: var(--radius-2xl);
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.12);
  box-shadow: 0 18px 60px rgba(0, 0, 0, 0.18);
}

.dark .device-sheet--panel {
  background: rgb(var(--gray-800));
  border-color: rgba(255, 255, 255, 0.14);
}

/* The accent hairline is drawn as a ring OVER the theme's own edge rather than
   as `border-color`: in glass mode the global surface rule sets the border, and
   a scoped `border-color` here would lose to it. Same reason it survives the
   alarm flash — see below. */
.device-sheet--panel::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  border: 1px solid color-mix(in srgb, var(--sheet-accent) 34%, transparent);
  pointer-events: none;
}

.device-sheet--panel.device-sheet--active::after {
  border-color: color-mix(in srgb, var(--sheet-accent) 52%, transparent);
}

/* Alarm wins: while the alarm flashes, the accent ring stands down and the
   animation owns the edge (kept from `DeviceControlModal.vue`, colours and
   timings unchanged). */
.device-sheet--panel.device-sheet--alarm {
  animation: device-sheet-alarm-pulse 1.2s ease-in-out infinite;
}

.device-sheet--panel.device-sheet--alarm::after {
  border-color: rgb(220, 38, 38);
}

/* The head reads the alarm in both variants — the old modal's glyph went red
   too (`deviceStatusColor` → the alarm's rgb(220, 38, 38)). The doubled class
   is load-bearing (card t_2a1c5a47): at one class this rule ties with
   `.dark .device-sheet-circle` and lost to it on source order, so the alarm
   glyph went grey in dark mode while its ring stayed red; two classes on the
   root take it to (0,4,0), above the dark theme's own glyph rule and above
   `--active` in both themes — the alarm keeps winning, as this comment always
   claimed. */
.device-sheet.device-sheet--alarm .device-sheet-circle {
  color: rgb(220, 38, 38);
  border-color: rgba(220, 38, 38, 0.55);
  box-shadow: 0 0 14px rgba(220, 38, 38, 0.28);
}

@keyframes device-sheet-alarm-pulse {
  0% {
    border-color: rgb(220, 38, 38);
    box-shadow:
      0 0 12px rgba(220, 38, 38, 0.6),
      0 0 28px rgba(220, 38, 38, 0.3);
  }
  50% {
    border-color: rgba(220, 38, 38, 0.5);
    box-shadow:
      0 0 6px rgba(220, 38, 38, 0.3),
      0 0 14px rgba(220, 38, 38, 0.15);
  }
  100% {
    border-color: rgb(220, 38, 38);
    box-shadow:
      0 0 12px rgba(220, 38, 38, 0.6),
      0 0 28px rgba(220, 38, 38, 0.3);
  }
}

/* ── the head ─────────────────────────────────────────────────────────────── */

.device-sheet-head {
  display: flex;
  align-items: center;
  gap: calc(0.75rem * var(--ui-space-factor));
  padding: calc(0.875rem * var(--ui-space-factor)) calc(1rem * var(--ui-space-factor))
    calc(0.8125rem * var(--ui-space-factor));
  border-bottom: 1px solid var(--glass-edge-soft);
  flex-shrink: 0;
}

.device-sheet-circle {
  width: 2.625rem;
  height: 2.625rem;
  flex-shrink: 0;
  /* The motion badge anchors to this circle's corner. */
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--icon-circle-fill);
  border: 1px solid rgba(148, 163, 184, 0.35);
  color: rgb(var(--gray-500));
  transition:
    color 0.2s,
    border-color 0.2s,
    box-shadow 0.2s;
}

.dark .device-sheet-circle {
  color: rgb(var(--gray-400));
}

/* The active glyph tints in dark mode too (card t_2a1c5a47): at (0,2,0) the
   `--active` rule below lost `color` to the dark theme's own (0,3,0) rule —
   the ring tinted green while the glyph stayed grey, and dark disagreed with
   light. Same property, same theme gate, later in source, so the accent wins
   here; the alarm rule above sits at (0,4,0) and still stands it down. */
.dark .device-sheet-circle--active {
  color: var(--sheet-accent);
}

.device-sheet-circle--active {
  color: var(--sheet-accent);
  border-color: color-mix(in srgb, var(--sheet-accent) 40%, rgba(148, 163, 184, 0.3));
  box-shadow: 0 0 14px color-mix(in srgb, var(--sheet-accent) 16%, transparent);
}

.device-sheet-glyph {
  font-size: 1.3125rem;
  line-height: 1;
}

.device-sheet-thumb {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  /* Iconic, never a crop (owner, 25.10.2026: "should display images iconic",
     "not cover"): a cut-out product must stay whole inside the ring, so a pump's
     strainer pot or a shade's cord can never be clipped by the circle. */
  object-fit: contain;
  object-position: center;
}

/*
 * A room-view sensor's thumbnail is a photograph of the ROOM, not of the device
 * (owner, 24.10.2026: "it should be black end white. and on motion we turn it in
 * color"). It stays monochrome while the room is still and floods back to colour
 * the moment the contact reports — the transition is what makes the colour read
 * as the event, so it lives on the thumb and not on the card.
 */
.device-sheet--mono .device-sheet-thumb {
  filter: grayscale(1) contrast(1.03) brightness(0.97);
  transition: filter 0.5s ease;
}

.device-sheet--mono.device-sheet--active .device-sheet-thumb {
  filter: none;
}

/*
 * Motion detected breathes on the CARD, in the light widget's own language
 * (`.motion-indicator` in style.css, owner 24.10.2026: "like breathing bg,,
 * border.."): same 2.2 s cadence, same amber. A security alarm outranks it and
 * keeps its red flash untouched.
 */
.device-sheet--motion:not(.device-sheet--alarm) {
  animation: device-sheet-motion-breathe 2.2s ease-in-out infinite;
}

.device-sheet--panel.device-sheet--motion:not(.device-sheet--alarm)::after {
  border-color: color-mix(in srgb, #f59e0b 62%, transparent);
}

@keyframes device-sheet-motion-breathe {
  0%,
  100% {
    box-shadow:
      0 0 0 1px color-mix(in srgb, #f59e0b 30%, transparent),
      0 0 10px color-mix(in srgb, #f59e0b 12%, transparent);
  }
  50% {
    box-shadow:
      0 0 0 1px color-mix(in srgb, #f59e0b 70%, transparent),
      0 0 22px color-mix(in srgb, #f59e0b 30%, transparent);
  }
}

.device-sheet-motion-badge {
  position: absolute;
  right: -0.125rem;
  bottom: -0.125rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.0625rem;
  height: 1.0625rem;
  border-radius: 999px;
  background: #f59e0b;
  color: #1c1917;
  font-size: 0.75rem;
  line-height: 1;
  font-family: 'Material Symbols Outlined', sans-serif;
  font-variation-settings:
    'FILL' 1,
    'wght' 500,
    'GRAD' 0,
    'opsz' 20;
  box-shadow: 0 0 0 2px var(--icon-circle-fill);
  animation: motion-indicator-pulse 2.2s ease-in-out infinite;
}

@media (prefers-reduced-motion: reduce) {
  .device-sheet--motion:not(.device-sheet--alarm),
  .device-sheet-motion-badge {
    animation: none;
  }
}

.device-sheet-title {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.device-sheet-name {
  margin: 0;
  font-size: 0.9688rem;
  font-weight: 650;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.device-sheet-room {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0;
  max-width: 100%;
  border: 0;
  background: none;
  font: inherit;
  font-size: 0.7188rem;
  color: rgb(var(--gray-500));
  text-align: left;
  cursor: pointer;
  min-width: 0;
}

.device-sheet-room:hover {
  text-decoration: underline;
}

.dark .device-sheet-room {
  color: rgb(var(--gray-400));
}

.device-sheet-room--empty {
  cursor: default;
  opacity: 0.7;
}

.device-sheet-room-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.device-sheet-right {
  display: flex;
  align-items: center;
  gap: 0.4375rem;
  flex-shrink: 0;
}

.device-sheet-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: calc(0.25rem * var(--ui-space-factor)) calc(0.75rem * var(--ui-space-factor));
  font-size: 0.75rem;
  font-weight: 650;
  white-space: nowrap;
  background: color-mix(in srgb, var(--sheet-accent) 15%, transparent);
  border: 1px solid color-mix(in srgb, var(--sheet-accent) 38%, transparent);
  color: var(--sheet-accent);
}

.device-sheet-pill--off {
  background: rgba(148, 163, 184, 0.12);
  border-color: var(--glass-edge);
  color: rgb(var(--gray-500));
}

.dark .device-sheet-pill--off {
  color: rgb(var(--gray-400));
}

.device-sheet-iconbtn {
  width: 2rem;
  height: 2rem;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  border-radius: 50%;
  background: transparent;
  color: rgb(var(--gray-400));
  font-size: 1.0625rem;
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s;
}

.device-sheet-iconbtn:hover {
  background: rgba(148, 163, 184, 0.16);
  color: rgb(var(--gray-600));
}

.dark .device-sheet-iconbtn:hover {
  color: rgb(var(--gray-200));
}

/* ── narrow hosts (card t_f60a8a96) ────────────────────────────────────────
 * Measured before this: at the dashboard card's declared minimum 154×86 the
 * head had 132 px and its fixed content asked for ~153 — the name and room
 * squeezed to 0 px and the close hung outside the card (clipped). Every band
 * below is read off the sheet's own width, so hosts need no flag; part sizes
 * are the measured ones (circle 42, icon button 32, phone-modal head 288).
 *
 * Band 1 — a tighter head. Below 298 px the host is a phone-width modal
 * (viewport 320 → 288) or the embedded card, where the same furniture must
 * fit in roughly a third of the room.
 */
@container device-sheet (max-width: 298px) {
  .device-sheet-head {
    gap: calc(0.5rem * var(--ui-space-factor));
    padding: calc(0.625rem * var(--ui-space-factor)) calc(0.75rem * var(--ui-space-factor))
      calc(0.5625rem * var(--ui-space-factor));
  }

  .device-sheet-right {
    gap: calc(0.375rem * var(--ui-space-factor));
  }

  .device-sheet-pill {
    padding: calc(0.125rem * var(--ui-space-factor)) calc(0.5rem * var(--ui-space-factor));
  }
}

/* Band 2 — the gear goes. Below 174 px the gear drives the name under 60 px
 * (measured at the card's declared 154 box — head 132: 32 px of gear where the
 * name then keeps only 18; without it the name keeps 50, and at 230's default
 * the gear fits beside a 94 px name). The room page's own full-control cards
 * span the section grid's tracks (`.room-device-slot--full`) and never come
 * this narrow, so its settings door — the head is the only place a room card
 * carries one — is untouched there; this band is the embedded dashboard card's.
 * The close is NOT part of the band: it is drawn only where `dismissible`,
 * which is the modal, and the modal is never this narrow. */
@container device-sheet (max-width: 174px) {
  .device-sheet-iconbtn--gear {
    display: none;
  }
}

/* ── the body ─────────────────────────────────────────────────────────────── */

/* No scroller here: the host owns the scrolling container (the modal's own body),
   so the sheet never grows a second scrollbar inside it. */
.device-sheet-body {
  display: flex;
  flex-direction: column;
  gap: calc(1rem * var(--ui-space-factor));
  padding: calc(1.125rem * var(--ui-space-factor)) calc(1.125rem * var(--ui-space-factor))
    calc(0.875rem * var(--ui-space-factor));
  min-width: 0;
}
</style>
